import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { stripLeadingZeros } from "./utils";

export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  step,
  disabled,
  className,
}: {
  value: number;
  onValueChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}) {
  const [text, setText] = useState<string>(String(value));

  useEffect(() => {
    if (Number(text) !== value) setText(String(value));
  }, [value]);

  const commit = (raw: string) => {
    let s = stripLeadingZeros(raw);
    if (s === "" || s === "-" || s === "." || s === "-.") {
      setText(s);
      return;
    }
    let n = Number(s);
    if (Number.isNaN(n)) {
      setText(s);
      return;
    }
    if (min !== undefined && n < min) n = min;
    if (max !== undefined && n > max) {
      n = max;
      s = String(max);
    }
    setText(s);
    onValueChange(n);
  };

  return (
    <Input
      type="number"
      inputMode="decimal"
      value={text}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => commit(e.target.value)}
      onWheel={(e) => e.currentTarget.blur()}
      onBlur={() => setText(String(value))}
      className={className}
    />
  );
}

export function InlineNumberInput({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  suffix,
  tooltip,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  tooltip?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-sm text-foreground font-medium">{label}</span>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className="w-20 h-8 text-sm text-center rounded-md bg-card tabular-nums"
        />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}
